import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';

import { useRootData } from '../hook/use-root-data';

/** `?keyboardDebug=1` turns the readout on. Outside production only. */
export const KEYBOARD_DEBUG_PARAM = 'keyboardDebug';

/** The open sheet, whichever gate it belongs to. */
const SHEET_SELECTOR = '[data-slot="drawer-content"]';

interface Sample {
    innerHeight: number;
    visualHeight: number;
    offsetTop: number;
    /** What the sheet's own style resolves to, not what we think we set. */
    sheetBottom: string;
    sheetMaxHeight: string;
    sheetTop: number;
    sheetHeight: number;
    /**
     * Where the sheet's foot sits against the top of the keyboard, or null
     * with no sheet open — a nought there would read as resting perfectly.
     */
    footGap: number | null;
    event: string;
}

const read = (event: string): Sample | null => {
    const visual = window.visualViewport;
    if (!visual) return null;

    const sheet = document.querySelector(SHEET_SELECTOR);
    const style = sheet ? getComputedStyle(sheet) : null;
    const box = sheet?.getBoundingClientRect();

    // The keyboard's top edge in layout coordinates, and how far the sheet's
    // foot sits above it. Nought means the sheet is resting on the keyboard,
    // which is the whole point; positive means the page shows in the gap.
    const keyboardTop = visual.offsetTop + visual.height;
    const footGap = box
        ? Math.round(keyboardTop - (box.top + box.height))
        : null;

    return {
        innerHeight: Math.round(window.innerHeight),
        visualHeight: Math.round(visual.height),
        offsetTop: Math.round(visual.offsetTop),
        sheetBottom: style?.bottom ?? '—',
        sheetMaxHeight: style?.maxHeight ?? '—',
        sheetTop: Math.round(box?.top ?? 0),
        sheetHeight: Math.round(box?.height ?? 0),
        footGap,
        event,
    };
};

/**
 * A live readout of the numbers behind the iOS keyboard geometry, for reading
 * off a real phone.
 *
 * The sheets are positioned from `visualViewport` because iOS does not shrink
 * the layout viewport for a keyboard. Nothing in a headless browser has a
 * software keyboard, so the only way to see which of those numbers is wrong is
 * to put them on the screen of a device that does.
 *
 * Unlike the hook it exists to check, this listens to `scroll` as well as
 * `resize` — it only reads, so it cannot start the feedback loop that keeps
 * `useIOSKeyboardOffset` on `resize` alone. `event` names whichever fired last,
 * which is what says whether a stale `offsetTop` is the problem.
 */
const Readout = () => {
    // Nothing is read until the effect runs: `read` touches `window`, and the
    // portal needs a `document.body` the server does not have.
    const [sample, setSample] = useState<Sample | null>(null);

    // The last viewport event to fire, which is the tell for a stale reading:
    // a sheet that is wrong while this says `resize` was positioned before
    // Safari scrolled and never corrected.
    const lastEvent = useRef('mount');

    useEffect(() => {
        const visual = window.visualViewport;
        if (!visual) return;

        const sampleNow = () => setSample(read(lastEvent.current));
        const after = (name: string) => () => {
            lastEvent.current = name;
            sampleNow();
        };
        const onResize = after('resize');
        const onScroll = after('scroll');
        const onFocus = after('focusin');

        sampleNow();
        visual.addEventListener('resize', onResize);
        visual.addEventListener('scroll', onScroll);
        document.addEventListener('focusin', onFocus);
        // A sheet opening fires none of the three, and the numbers have to be
        // on screen before the first tap for the before/after to mean anything.
        const tick = setInterval(sampleNow, 200);

        return () => {
            clearInterval(tick);
            visual.removeEventListener('resize', onResize);
            visual.removeEventListener('scroll', onScroll);
            document.removeEventListener('focusin', onFocus);
        };
    }, []);

    if (!sample) return null;

    const keyboard = sample.innerHeight - sample.visualHeight;

    return createPortal(
        // On the body, above `z-toast`: a sheet is `z-modal` and portals to the
        // body itself, so anything rendered inside the page would sit under the
        // thing it is measuring.
        <div
            className="pointer-events-none fixed inset-x-0 top-0 z-[400] bg-black/80 px-2 py-1 font-mono text-[10px] leading-[1.35] text-white"
            data-testid="keyboard-debug-readout"
        >
            <div>
                inner {sample.innerHeight} · vv {sample.visualHeight} · offTop{' '}
                {sample.offsetTop} · kbd {keyboard}
            </div>
            <div>
                bottom {sample.sheetBottom} · maxH {sample.sheetMaxHeight}
            </div>
            <div>
                sheet top {sample.sheetTop} h {sample.sheetHeight} · footGap{' '}
                <span
                    className={
                        sample.footGap === null || sample.footGap === 0
                            ? ''
                            : 'text-red-300'
                    }
                >
                    {sample.footGap ?? '—'}
                </span>{' '}
                · {sample.event}
            </div>
        </div>,
        document.body,
    );
};

/**
 * Mounts the readout when `?keyboardDebug=1` is on the URL, outside production.
 *
 * The gate is the same one the dynamic favicon uses — stage, localhost and the
 * team servers — so it is reachable on dev.archipro.co.nz, which is where the
 * phone testing happens.
 */
export const KeyboardDebugReadout = () => {
    const { env } = useRootData();
    const [searchParams] = useSearchParams();

    const outsideProduction =
        env.stage !== 'Production' ||
        env.host === 'localhost' ||
        env.host.includes('.team.archipro.');

    if (!outsideProduction) return null;
    if (searchParams.get(KEYBOARD_DEBUG_PARAM) !== '1') return null;

    return <Readout />;
};
